import {
  Box,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from "@mui/material";
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";

// Tipo para as comissões individuais
interface CommissionDetail {
  id: string; // O ID da comissão que será usado no POST
  referralId: string;
  commissionAmount: string;
  commissionPercentage: string;
  status: "PAID" | "PENDING";
  createdAt: string;
}

// Tipo de resumo por usuário
interface UserCommissionSummaryRow {
  id: string; // userId
  userName: string;
  userEmail: string;
  pixKey: string;
  pixKeyName: string;
  pixKeyType: string;
  totalCommissions: number;
  totalPending: number;
  totalPaid: number;
  hasPendingCommissions: boolean;
  commissionsCount: number;
  lastCommissionDate: string;
  totalCommissionsFormatted: string;
  totalPendingFormatted: string;
  totalPaidFormatted: string;
  rawCommissions?: CommissionDetail[];
}

interface ListaComissoesProps {
  token: string;
  role: "MASTER" | "PARTNER" | "ANALYST";
}

const DATAGRID_ID = "commission-data-grid";

// Função utilitária para formatar a data
const formatDateToNotes = () => {
  const date = new Date();
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Função utilitária para traduzir o status
const translateStatus = (status: "PAID" | "PENDING") => {
  switch (status) {
    case "PAID":
      return "PAGO";
    case "PENDING":
      return "PENDENTE";
    default:
      return status;
  }
};

// -------------------------------------------------------------
// COMPONENTE: MODAL DE DETALHES DE COMISSÕES INDIVIDUAIS
// -------------------------------------------------------------
const DetailCommissionDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  commissions: CommissionDetail[];
}> = ({ open, onClose, commissions }) => {
  const commissionsToShow = commissions.sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  const totalValue = commissions
    .reduce((acc, c) => acc + parseFloat(c.commissionAmount), 0)
    .toFixed(2);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Detalhes de {commissions.length} Comissões</DialogTitle>
      <DialogContent dividers>
        <Typography variant="h6" gutterBottom>
          Valor Total: R$ {totalValue}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 2,
          }}
        >
          {commissionsToShow.map((c, index) => (
            <Box
              key={c.id}
              sx={{
                p: 1.5,
                borderRadius: 1,
                border: `1px solid ${
                  c.status === "PENDING" ? "#ff9800" : "#4caf50"
                }`,
                bgcolor: c.status === "PENDING" ? "#fff3e0" : "#e8f5e9",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Comissão #{index + 1}
              </Typography>
              <Typography variant="body2">
                Valor:{" "}
                <span style={{ fontWeight: "bold" }}>
                  R$ {parseFloat(c.commissionAmount).toFixed(2)}
                </span>
              </Typography>
              <Typography variant="body2">
                Status:{" "}
                <span
                  style={{
                    color: c.status === "PENDING" ? "#ff9800" : "#4caf50",
                  }}
                >
                  {/* 🚀 APLICAÇÃO DA TRADUÇÃO */}
                  {translateStatus(c.status)}
                </span>
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Data: {new Date(c.createdAt).toLocaleDateString("pt-BR")}
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                ID: {c.id}
              </Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
// -------------------------------------------------------------

const ListaComissoes: React.FC<ListaComissoesProps> = ({ token, role }) => {
  const [rows, setRows] = useState<GridRowsProp<UserCommissionSummaryRow>>([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [filterStatus, setFilterStatus] = useState<"PAID" | "PENDING" | "ALL">(
    "ALL"
  );

  // --- ESTADOS DO MODAL DE PAGAMENTO ---
  const [openMarkPaidDialog, setOpenMarkPaidDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [pendingCommissions, setPendingCommissions] = useState<
    CommissionDetail[]
  >([]);
  const [paymentNotes, setPaymentNotes] = useState("");
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);

  // --- ESTADOS DO MODAL DE DETALHES ---
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [detailCommissions, setDetailCommissions] = useState<
    CommissionDetail[]
  >([]);
  // ---------------------------------------------

  // --- FUNÇÕES DO MODAL DE DETALHES ---
  const handleOpenDetailDialog = (userId: string) => {
    const userRow = rows.find((r) => r.id === userId);

    if (!userRow || !userRow.rawCommissions) {
      alert("Dados do usuário ou comissões não encontrados.");
      return;
    }
    setDetailCommissions(userRow.rawCommissions);
    setOpenDetailDialog(true);
  };

  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
    setDetailCommissions([]);
  };
  // ------------------------------------

  // --- FUNÇÕES DE CONTROLE DO MODAL DE PAGAMENTO ---

  const handleOpenMarkPaidDialog = (userId: string) => {
    const userRow = rows.find((r) => r.id === userId);

    if (!userRow || !userRow.rawCommissions) {
      alert("Dados do usuário ou comissões não encontrados.");
      return;
    }

    const pending = userRow.rawCommissions.filter(
      (c) => c.status === "PENDING"
    );

    if (pending.length === 0) {
      alert("Este usuário não possui comissões pendentes para pagamento.");
      return;
    }

    setSelectedUserId(userId);
    setPendingCommissions(pending);
    setPaymentNotes(`Pagamento realizado via PIX em ${formatDateToNotes()}`);
    setOpenMarkPaidDialog(true);
  };

  const handleCloseMarkPaidDialog = () => {
    setOpenMarkPaidDialog(false);
    setSelectedUserId(null);
    setPendingCommissions([]);
    setPaymentNotes("");
  };

  const handleMarkPaid = async () => {
    if (!selectedUserId || pendingCommissions.length === 0) return;
    setIsMarkingPaid(true);

    let successCount = 0;
    let errorCount = 0;
    const totalCommissions = pendingCommissions.length;

    for (const commission of pendingCommissions) {
      try {
        const url = `https://api.anuncio360.com.br/admin/referral-commissions/${commission.id}/mark-paid`;

        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            notes: paymentNotes,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({
            message: `Erro desconhecido ao marcar como pago a comissão ${commission.id}.`,
          }));
          throw new Error(
            errorData.message ||
              `Falha ao marcar comissão ${commission.id}. Status: ${response.status}`
          );
        }
        successCount++;
      } catch (error) {
        console.error(
          `Erro ao marcar comissão ${commission.id} como paga:`,
          error
        );
        errorCount++;
      }
    }

    if (errorCount === 0) {
      alert(
        `Sucesso! ${successCount} de ${totalCommissions} comissões marcadas como pagas.`
      );
    } else {
      alert(
        `Processo concluído com ${successCount} sucessos e ${errorCount} falhas. Recarregando a lista...`
      );
    }

    fetchCommissions(
      paginationModel.page,
      paginationModel.pageSize,
      filterStatus
    );
    handleCloseMarkPaidDialog();
    setIsMarkingPaid(false);
  };
  // -------------------------------------------------------------

  // --- Função para Buscar Dados com Fetch ---
  const fetchCommissions = useCallback(
    async (
      page: number,
      pageSize: number,
      status: "PAID" | "PENDING" | "ALL"
    ) => {
      setLoading(true);
      const apiPage = page + 1;
      let url = `https://api.anuncio360.com.br/admin/referral-commissions?page=${apiPage}&limit=${pageSize}`;
      if (status !== "ALL") {
        url += `&status=${status}`;
      }

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(
            `Erro na API: ${response.status} ${response.statusText}`
          );
        }

        const responseData = await response.json();

        const apiData: UserCommissionSummaryRow[] = responseData.users.map(
          (user: any) => {
            const totalCommissionsValue =
              parseFloat(user.totalCommissions) || 0;
            const totalPendingValue = parseFloat(user.totalPending) || 0;
            const totalPaidValue = parseFloat(user.totalPaid) || 0;

            const rawCommissions: CommissionDetail[] = (
              user.commissions || []
            ).map((c: any) => ({
              id: c.id,
              referralId: c.referralId,
              commissionAmount: c.commissionAmount,
              commissionPercentage: c.commissionPercentage,
              status: c.status,
              createdAt: c.createdAt,
            }));

            return {
              id: user.userId,
              userName: user.userName,
              userEmail: user.userEmail,
              pixKey: user.pixKey,
              pixKeyName: user.pixKeyName,
              pixKeyType: user.pixKeyType,
              totalCommissions: totalCommissionsValue,
              totalPending: totalPendingValue,
              totalPaid: totalPaidValue,
              hasPendingCommissions: user.hasPendingCommissions,
              commissionsCount: user.commissionsCount,
              lastCommissionDate: user.lastCommissionDate
                ? new Date(user.lastCommissionDate).toLocaleDateString("pt-BR")
                : "N/A",
              totalCommissionsFormatted: `R$ ${totalCommissionsValue.toFixed(
                2
              )}`,
              totalPendingFormatted: `R$ ${totalPendingValue.toFixed(2)}`,
              totalPaidFormatted: `R$ ${totalPaidValue.toFixed(2)}`,
              rawCommissions: rawCommissions,
            };
          }
        );

        setRows(apiData);
        setTotalRowCount(responseData.total);
      } catch (error) {
        console.error("Erro ao buscar comissões:", error);
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    fetchCommissions(
      paginationModel.page,
      paginationModel.pageSize,
      filterStatus
    );
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    filterStatus,
    fetchCommissions,
  ]);

  const columns: GridColDef<UserCommissionSummaryRow>[] = useMemo(
    () => [
      {
        field: "userName",
        headerName: "Usuário",
        minWidth: 150,
        flex: 1,
      },
      { field: "pixKey", headerName: "Chave Pix", minWidth: 150 },
      { field: "pixKeyName", headerName: "Nome Pix", minWidth: 150 },
      { field: "pixKeyType", headerName: "Tipo Pix", minWidth: 100 },
      {
        field: "totalPendingFormatted",
        headerName: "Pendente",
        minWidth: 120,
        type: "string",
      },
      {
        field: "totalPaidFormatted",
        headerName: "Pago",
        minWidth: 120,
        type: "string",
      },
      {
        field: "hasPendingCommissions",
        headerName: "Status",
        minWidth: 100,
        renderCell: (params) => {
          const isPending = params.row.totalPending > 0;
          return (
            <Box
              sx={{
                color: isPending ? "warning.main" : "success.main",
                fontWeight: "bold",
                fontSize: "0.8rem",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {isPending ? (
                <MoneyOffIcon fontSize="inherit" />
              ) : (
                <CheckCircleIcon fontSize="inherit" />
              )}
              {isPending ? "PENDENTE" : "PAGO"}
            </Box>
          );
        },
      },

      {
        field: "action",
        headerName: "Ação",
        minWidth: 220,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const isPending = params.row.totalPending > 0;
          const userId = params.row.id;
          const pendingCount =
            params.row.rawCommissions?.filter((c) => c.status === "PENDING")
              .length || 0;

          return (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                variant="outlined"
                color="info"
                size="small"
                onClick={() => handleOpenDetailDialog(userId)}
                sx={{ whiteSpace: "nowrap" }}
              >
                Detalhes ({params.row.commissionsCount})
              </Button>

              {isPending && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => handleOpenMarkPaidDialog(userId)}
                  disabled={role === "ANALYST"}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Pagar ({pendingCount})
                </Button>
              )}
            </Box>
          );
        },
      },
    ],
    [handleOpenMarkPaidDialog, handleOpenDetailDialog, role]
  );

  const handleExportPdf = () => {
    // ... (Função de exportação para PDF inalterada)
    const input = document.getElementById(DATAGRID_ID);

    if (input) {
      const paginationElement = input.querySelector(
        ".MuiDataGrid-footerContainer"
      );
      const toolbarElement = input.querySelector(
        ".MuiDataGrid-toolbarContainer"
      );

      if (paginationElement)
        paginationElement.setAttribute("style", "display: none;");
      if (toolbarElement)
        toolbarElement.setAttribute("style", "display: none;");

      html2canvas(input, {
        scale: 2,
      }).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("l", "mm", "a4");
        const imgWidth = 280;
        const pageHeight = 210;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, "PNG", 5, position + 5, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 5, position + 5, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        if (paginationElement) paginationElement.removeAttribute("style");
        if (toolbarElement) toolbarElement.removeAttribute("style");

        pdf.save(
          `Comissoes-${filterStatus}-${new Date()
            .toISOString()
            .slice(0, 10)}.pdf`
        );
      });
    }
  };

  const totalAmountToPay = useMemo(() => {
    return pendingCommissions
      .reduce((acc, c) => acc + parseFloat(c.commissionAmount), 0)
      .toFixed(2);
  }, [pendingCommissions]);

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      {/* CABEÇALHO E CONTROLES */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 2,
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }}
        >
          Comissões por Usuário
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            width: { xs: "100%", sm: "auto" },
            flexDirection: { xs: "row", sm: "row" },
          }}
        >
          {/* Seletor de Status */}
          <FormControl
            sx={{ minWidth: { xs: 120, sm: 150 }, flexGrow: 1 }}
            size="small"
          >
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={filterStatus}
              label="Status"
              onChange={(e) =>
                setFilterStatus(e.target.value as "PAID" | "PENDING" | "ALL")
              }
            >
              <MenuItem value="ALL">Todos</MenuItem>
              <MenuItem value="PENDING">Pendentes</MenuItem>
              <MenuItem value="PAID">Pagos</MenuItem>
            </Select>
          </FormControl>

          {/* Botão de Download PDF */}
          <Button
            variant="contained"
            onClick={handleExportPdf}
            startIcon={<DownloadIcon />}
            sx={{ flexShrink: 0 }}
          >
            <Box
              component="span"
              sx={{ display: { xs: "none", sm: "inline" } }}
            >
              Baixar PDF
            </Box>
            <Box
              component="span"
              sx={{ display: { xs: "inline", sm: "none" } }}
            >
              PDF
            </Box>
          </Button>
        </Box>
      </Box>

      {/* DATAGRID */}
      <Box
        id={DATAGRID_ID + "-container"}
        sx={{
          width: "100%",
          overflowX: "auto",
        }}
      >
        <div id={DATAGRID_ID} style={{ minWidth: "700px" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            paginationMode="server"
            rowCount={totalRowCount}
            pageSizeOptions={[10, 20, 50]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            disableRowSelectionOnClick
            sx={{
              height: 600,
              width: "100%",
              "& .MuiDataGrid-row:nth-of-type(odd)": {
                backgroundColor: "#f5f5f5",
              },
              "& .MuiDataGrid-cell": {
                padding: "8px",
              },
              "& .MuiDataGrid-row.Mui-selected": {
                backgroundColor: "#e0e0e0 !important",
              },
            }}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  userEmail: false,
                  totalCommissions: false,
                  totalCommissionsFormatted: false,
                  lastCommissionDate: false,
                },
              },
            }}
          />
        </div>
      </Box>

      {/* --- MODAL DE CONFIRMAÇÃO DE PAGAMENTO --- */}
      <Dialog
        open={openMarkPaidDialog}
        onClose={handleCloseMarkPaidDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Confirmar Pagamento de Comissões</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" gutterBottom>
            Você está prestes a marcar **{pendingCommissions.length}** comissões
            pendentes do usuário **
            {rows.find((r) => r.id === selectedUserId)?.userName}** como pagas.
          </Typography>
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            Esta ação é **irreversível** e fará uma requisição para cada
            comissão listada.
          </Typography>

          <Box
            sx={{
              my: 2,
              p: 2,
              border: "1px solid #ccc",
              borderRadius: 1,
              maxHeight: 200,
              overflowY: "auto",
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Detalhes das Comissões Pendentes:
            </Typography>
            {pendingCommissions.map((c, index) => (
              <Box
                key={c.id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  borderBottom:
                    index < pendingCommissions.length - 1
                      ? "1px dotted #eee"
                      : "none",
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  Comissão ID: {c.id.substring(0, 8)}...
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  R$ {parseFloat(c.commissionAmount).toFixed(2)}
                </Typography>
              </Box>
            ))}
            <Typography
              variant="subtitle1"
              sx={{ mt: 1, borderTop: "1px solid #ccc", pt: 1 }}
            >
              Total a Pagar:{" "}
              <span style={{ color: "green" }}>R$ {totalAmountToPay}</span>
            </Typography>
          </Box>

          <TextField
            label="Notas do Pagamento (Obrigatório)"
            multiline
            rows={4}
            fullWidth
            margin="normal"
            value={paymentNotes}
            onChange={(e) => setPaymentNotes(e.target.value)}
            disabled={isMarkingPaid}
            helperText={`A nota será usada no registro de cada pagamento. A data de hoje (${formatDateToNotes()}) será incluída por padrão.`}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseMarkPaidDialog}
            color="secondary"
            disabled={isMarkingPaid}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleMarkPaid}
            color="primary"
            variant="contained"
            disabled={
              isMarkingPaid ||
              paymentNotes.trim().length < 5 ||
              pendingCommissions.length === 0
            }
            startIcon={
              isMarkingPaid ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <CheckCircleIcon />
              )
            }
          >
            {isMarkingPaid
              ? "Processando..."
              : `Pagar ${pendingCommissions.length} Comissões`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL DE DETALHES DE COMISSÕES INDIVIDUAIS */}
      <DetailCommissionDialog
        open={openDetailDialog}
        onClose={handleCloseDetailDialog}
        commissions={detailCommissions}
      />
    </Box>
  );
};

export default ListaComissoes;
